import React, { useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import authApi from "../../../service/IdApi";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import { Column } from "primereact/column";
import { Button } from 'primereact/button';
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { useAppContext } from "../../../context/appContext";

const Promos = () => {
    const { displayAlert } = useAppContext();
    const [promos, setPromos] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const dataTable = useRef(null);
    const [showDetailsDialog, setShowDetailsDialog] = useState(false);
    const [promoTableState, setPromoTableState] = useState(() => {
        return {
            first: 0,
            rows: 25,
            page: 1,
            sortField: "createdAt",
            sortOrder: -1
        };
    });
    const [selectedPromo, setSelectedPromo] = useState(null);
    const [selectedPromos, setSelectedPromos] = useState([]);

    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [createPromo, setCreatePromo] = useState({
        promoCode: "",
        name: "",
        type: "user",
        // Keep as a string (e.g. "01-25-2026") to match backend date formats directly.
        expiresAt: "",
        termInMonths: null,
        userCount: null,
        percentageOff: null,
        productCodesAll: true, // submit blank/null to backend
        productCodes: [], // e.g. ["operate", "engage", "promote", "pro"]
    });
    const [expiresAtError, setExpiresAtError] = useState(null);
    const [createLoading, setCreateLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const promoTypeOptions = useMemo(() => ([
        { label: "Percentage", value: "percentage" },
        { label: "User", value: "user" },
    ]), []);

    const productCodeOptions = useMemo(() => ([
        { label: "operate", value: "operate" },
        { label: "engage", value: "engage" },
        { label: "promote", value: "promote" },
        { label: "pro", value: "pro" },
    ]), []);

    const validateExpiresAt = (value) => {
        const v = (value || "").trim();
        if (!v) return null; // blank = never

        // Must be MM-dd-yyyy (e.g. 01-25-2026)
        const match = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-(\d{4})$/.exec(v);
        if (!match) return "Must be in MM-dd-yyyy format (e.g. 01-25-2026)";

        const month = Number(match[1]);
        const day = Number(match[2]);
        const year = Number(match[3]);

        // Validate actual calendar date (e.g. reject 02-30-2026)
        const dt = new Date(Date.UTC(year, month - 1, day));
        if (
            dt.getUTCFullYear() !== year ||
            dt.getUTCMonth() + 1 !== month ||
            dt.getUTCDate() !== day
        ) {
            return "Invalid calendar date (use MM-dd-yyyy, e.g. 01-25-2026)";
        }
        return null;
    };

    const { isLoading, isFetching, isError, data, refetch } = useQuery(["Promos", promoTableState], async () => {
        var body = {};
        body.first = promoTableState.first;
        body.rows = promoTableState.rows;
        body.sortField = promoTableState.sortField;
        body.sortOrder = promoTableState.sortOrder;
        const result = await authApi.post("admin/promos", body);
        setPromos(result.data.promos);
        setTotalRecords(result.data.totalRecords);
        setLoaded(true);
        return data;
    });

    const pageHeader = (
        <PageHeading pageTitle="Admin - Promo Codes">
            <div className="block">
                <Button label="Create" icon="pi pi-plus" className="mr-2" onClick={() => setShowCreateDialog(true)} />
                <Button
                    label="Delete Selected"
                    icon="pi pi-trash"
                    className="p-button-danger mr-2"
                    onClick={() => confirmDeleteSelected()}
                    disabled={!selectedPromos || selectedPromos.length === 0}
                    loading={deleteLoading}
                />
            </div>
        </PageHeading>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const onPage = (event) => {
        setPromoTableState(event);
    };

    const onSort = (event) => {
        setPromoTableState(event);
    };

    const exportCSV = (selectionOnly) => {
        dataTable.current.exportCSV({ selectionOnly });
    };

    const promoCodeBodyTemplate = (rowData) => {
        return (
            <div className="">
                <a href="#" onClick={(e) => rowClicked(e, rowData)}>
                    {rowData.promoCode}
                </a>
            </div>
        );
    };

    const getPromoId = (promo) => promo?._id || promo?.id;

    const rowClicked = (e, rowData) => {
        e.preventDefault();
        setSelectedPromo(rowData);
        setShowDetailsDialog(true);
    }

    const hideDetailsDialog = () => {
        setShowDetailsDialog(false);
    }

    const expiresAtBodyTemplate = (rowData) => {
        if (!rowData?.expiresAt) {
            return <span>Never</span>;
        }
        return (
            <span>{new Date(rowData.expiresAt).toLocaleDateString()}</span>
        );
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-text p-button-danger"
                tooltip="Delete promo"
                tooltipOptions={{ showDelay: 750 }}
                onClick={() => confirmDeleteSingle(rowData)}
                disabled={deleteLoading}
            />
        );
    };

    const confirmDeleteSingle = (promo) => {
        const promoId = getPromoId(promo);
        confirmDialog({
            header: "Confirm Delete",
            message: `Delete promo '${promo?.promoCode || promoId}'?`,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            acceptClassName: "p-button-danger",
            accept: async () => {
                await deletePromo(promoId);
            },
        });
    };

    const deletePromo = async (promoId) => {
        if (!promoId) return;
        try {
            setDeleteLoading(true);
            await authApi.delete(`admin/promo/${promoId}`);
            displayAlert("success", "Promo Deleted", "Promo has been deleted successfully");
            setSelectedPromo(null);
            setShowDetailsDialog(false);
            setSelectedPromos([]);
            await refetch();
        } catch (e) {
            displayAlert("error", "Delete Failed", "Unable to delete promo");
        } finally {
            setDeleteLoading(false);
        }
    };

    const confirmDeleteSelected = () => {
        const count = selectedPromos?.length || 0;
        if (count === 0) return;
        confirmDialog({
            header: "Confirm Delete",
            message: `Delete ${count} selected promo${count === 1 ? "" : "s"}?`,
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Delete",
            rejectLabel: "Cancel",
            acceptClassName: "p-button-danger",
            accept: async () => {
                await deleteSelectedPromos();
            },
        });
    };

    const deleteSelectedPromos = async () => {
        const ids = (selectedPromos || []).map(getPromoId).filter(Boolean);
        if (ids.length === 0) return;
        try {
            setDeleteLoading(true);
            const payload = ids.map((id) => ({ promoId: id }));
            await authApi.post("admin/promo/delete", payload);
            displayAlert("success", "Promos Deleted", `${ids.length} promo${ids.length === 1 ? "" : "s"} deleted successfully`);
            setSelectedPromos([]);
            await refetch();
        } catch (e) {
            displayAlert("error", "Delete Failed", "Unable to delete selected promos");
        } finally {
            setDeleteLoading(false);
        }
    };

    const resetCreatePromo = () => {
        setCreatePromo({
            promoCode: "",
            name: "",
            type: "user",
            expiresAt: "",
            termInMonths: null,
            userCount: null,
            percentageOff: null,
            productCodesAll: true,
            productCodes: [],
        });
        setExpiresAtError(null);
    };

    const createPromoSubmit = async () => {
        const expiresError = validateExpiresAt(createPromo.expiresAt);
        if (expiresError) {
            setExpiresAtError(expiresError);
            displayAlert("error", "Validation Error", expiresError);
            return;
        }

        try {
            setCreateLoading(true);

            const payload = {
                promoCode: (createPromo.promoCode || "").trim(),
                name: (createPromo.name || "").trim(),
                type: createPromo.type,
                // Backend expects a date string, not epoch millis:
                // - yyyy-MM-dd'T'HH:mm:ss.SSS'Z' (UTC) OR
                // - MM-dd-yyyy OR
                // - "MMM dd, yyyy, h:mm:ss a"
                expiresAt: (createPromo.expiresAt || "").trim() || null,
                termInMonths: createPromo.termInMonths,
                userCount: createPromo.userCount,
                percentageOff: createPromo.percentageOff,
                // Backend interprets null/empty as "default/all" behavior.
                productCodes: createPromo.productCodesAll ? null : (createPromo.productCodes?.length ? createPromo.productCodes : null),
            };

            if (!payload.promoCode) {
                displayAlert("error", "Validation Error", "Promo code is required");
                return;
            }

            await authApi.post("admin/promo", payload);
            displayAlert("success", "Promo Created", "Promo has been created successfully");
            setShowCreateDialog(false);
            resetCreatePromo();
            await refetch();
        } catch (e) {
            const msg = e?.response?.data || "Unable to create promo";
            displayAlert("error", "Create Failed", msg);
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex py-3 w-full">
                        <div className="flex flex-row">
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Button type="button" icon="pi pi-file-export" rounded onClick={() => exportCSV(false)} data-pr-tooltip="CSV" label="Export" />
                        </div>
                    </div>
                    <DataTable
                        ref={dataTable}
                        value={promos}
                        dataKey="_id"
                        selection={selectedPromos}
                        onSelectionChange={(e) => setSelectedPromos(e.value)}
                        selectionMode="multiple"
                        paginator
                        rows={promoTableState.rows}
                        rowsPerPageOptions={[25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} promos"
                        emptyMessage="No promos found."
                        lazy
                        first={promoTableState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={promoTableState.sortField}
                        sortOrder={promoTableState.sortOrder}
                        loading={isLoading || isFetching || !loaded}
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={actionBodyTemplate} headerStyle={{ width: "1%", minWidth: "1rem" }}></Column>
                        <Column body={promoCodeBodyTemplate} field="promoCode" header="Promo Code" sortable />
                        <Column field="name" header="Name" sortable />
                        <Column field="type" header="Type" sortable />
                        <Column body={expiresAtBodyTemplate} field="expiresAt" header="Expires On" sortable />
                    </DataTable>
                </div>
                <ConfirmDialog />
                <Dialog
                    visible={showDetailsDialog}
                    style={{ width: '50vw' }}
                    breakpoints={{ '1280px': '75vw', '641px': '95vw' }}
                    header="Promo Details"
                    modal
                    onHide={hideDetailsDialog}
                    className="p-fluid"
                >
                    {selectedPromo && (
                        <div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Promo Code</label>
                                <div className="col">{selectedPromo.promoCode}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Name</label>
                                <div className="col">{selectedPromo.name}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Type</label>
                                <div className="col">{selectedPromo.type}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Expires On</label>
                                <div className="col">{selectedPromo.expiresAt ? new Date(selectedPromo.expiresAt).toLocaleDateString() : "Never"}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Term in Months</label>
                                <div className="col">{selectedPromo.termInMonths}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>User Count</label>
                                <div className="col">{selectedPromo.userCount}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Discount Percent</label>
                                <div className="col">{selectedPromo.percentageOff}</div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Product Codes</label>
                                <div className="col">
                                    {(selectedPromo.productCodes && selectedPromo.productCodes.length > 0)
                                        ? selectedPromo.productCodes.join(", ")
                                        : "All"}
                                </div>
                            </div>
                            <div className="field grid mb-0">
                                <label className="col-fixed" style={{ width: '150px' }}>Created On</label>
                                <div className="col">{new Date(selectedPromo.createdAt).toLocaleDateString()}</div>
                            </div>
                        </div>

                    )}
                </Dialog>

                <Dialog
                    visible={showCreateDialog}
                    style={{ width: '520px' }}
                    header="Create Promo"
                    modal
                    className="p-fluid"
                    onHide={() => { if (!createLoading) { setShowCreateDialog(false); resetCreatePromo(); } }}
                    footer={
                        <div>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => { setShowCreateDialog(false); resetCreatePromo(); }}
                                disabled={createLoading}
                            />
                            <Button
                                label="Create"
                                icon="pi pi-check"
                                onClick={createPromoSubmit}
                                loading={createLoading}
                            />
                        </div>
                    }
                >
                    <div className="field">
                        <label htmlFor="promoCode" className="required">Promo Code</label>
                        <InputText
                            id="promoCode"
                            value={createPromo.promoCode}
                            onChange={(e) => {
                                const next = (e.target.value || "").toUpperCase();
                                setCreatePromo((p) => ({ ...p, promoCode: next }));
                            }}
                            autoFocus
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="name">Name</label>
                        <InputText
                            id="name"
                            value={createPromo.name}
                            onChange={(e) => setCreatePromo((p) => ({ ...p, name: e.target.value }))}
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="type">Type</label>
                        <Dropdown
                            id="type"
                            value={createPromo.type}
                            options={promoTypeOptions}
                            onChange={(e) => setCreatePromo((p) => {
                                const nextType = e.value;
                                // Clear fields that no longer apply so we don't send stale values.
                                if (nextType === "percentage") {
                                    return { ...p, type: nextType, userCount: null };
                                }
                                if (nextType === "user") {
                                    return { ...p, type: nextType, percentageOff: null };
                                }
                                return { ...p, type: nextType };
                            })}
                            placeholder="Select a type"
                        />
                    </div>
                    <div className="field">
                        <label htmlFor="expiresAt">Expires On</label>
                        <InputText
                            id="expiresAt"
                            value={createPromo.expiresAt}
                            onChange={(e) => {
                                const next = e.target.value;
                                setCreatePromo((p) => ({ ...p, expiresAt: next }));
                                const err = validateExpiresAt(next);
                                setExpiresAtError(err);
                            }}
                            onBlur={() => setExpiresAtError(validateExpiresAt(createPromo.expiresAt))}
                            placeholder="MM-dd-yyyy (e.g. 01-25-2026)"
                            className={expiresAtError ? "p-invalid" : ""}
                        />
                        {expiresAtError && (
                            <small className="block mt-1 p-error">{expiresAtError}</small>
                        )}
                    </div>
                    <div className="field">
                        <label htmlFor="termInMonths">Term in Months</label>
                        <InputNumber
                            id="termInMonths"
                            value={createPromo.termInMonths}
                            onValueChange={(e) => setCreatePromo((p) => ({ ...p, termInMonths: e.value }))}
                            min={0}
                            placeholder="e.g. 12"
                        />
                    </div>
                    {createPromo.type === "user" && (
                        <div className="field">
                            <label htmlFor="userCount">User Count</label>
                            <InputNumber
                                id="userCount"
                                value={createPromo.userCount}
                                onValueChange={(e) => setCreatePromo((p) => ({ ...p, userCount: e.value }))}
                                min={0}
                                placeholder="e.g. 5"
                            />
                        </div>
                    )}
                    {createPromo.type === "percentage" && (
                        <div className="field">
                            <label htmlFor="percentageOff">Discount Percent</label>
                            <InputNumber
                                id="percentageOff"
                                value={createPromo.percentageOff}
                                onValueChange={(e) => setCreatePromo((p) => ({ ...p, percentageOff: e.value }))}
                                min={0}
                                max={100}
                                suffix="%"
                                placeholder="e.g. 20"
                            />
                        </div>
                    )}
                    <div className="field">
                        <label>Product Codes</label>
                        <small className="block mb-3 text-600">
                            Which products should this promo apply to?
                        </small>
                        <div className="flex flex-column gap-2">
                            <div className="flex align-items-center gap-2">
                                <Checkbox
                                    inputId="productCodesAll"
                                    checked={createPromo.productCodesAll}
                                    onChange={(e) => {
                                        const checked = !!e.checked;
                                        setCreatePromo((p) => ({
                                            ...p,
                                            productCodesAll: checked,
                                            productCodes: checked ? [] : p.productCodes,
                                        }));
                                    }}
                                />
                                <label htmlFor="productCodesAll">All</label>
                            </div>
                            {!createPromo.productCodesAll && (
                                <div className="flex flex-column gap-2 pl-3">
                                    {productCodeOptions.map((opt) => {
                                        const checked = (createPromo.productCodes || []).includes(opt.value);
                                        return (
                                            <div key={opt.value} className="flex align-items-center gap-2">
                                                <Checkbox
                                                    inputId={`productCode_${opt.value}`}
                                                    checked={checked}
                                                    onChange={(e) => {
                                                        const nextChecked = !!e.checked;
                                                        setCreatePromo((p) => {
                                                            const current = p.productCodes || [];
                                                            const next = nextChecked
                                                                ? [...current, opt.value]
                                                                : current.filter((v) => v !== opt.value);
                                                            // If user unchecks everything, fall back to All.
                                                            if (next.length === 0) {
                                                                return { ...p, productCodesAll: true, productCodes: [] };
                                                            }
                                                            return { ...p, productCodesAll: false, productCodes: next };
                                                        });
                                                    }}
                                                />
                                                <label htmlFor={`productCode_${opt.value}`}>{opt.label}</label>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default Promos;
